import _ from "lodash";
import { FakeSiteGenerator } from "../components/Views/SiteManagement/demo/FakeSiteGen";
import {
  eulaVersion,
  getModificationStamp,
  getZooId,
  initAuth,
  initDb,
  initFunctions,
  AuthPersistence,
  firebase,
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
} from "../tools/conf";
import { isActiveUser } from "../utils/users/activeUsers";
import { updateIndividualPermissions } from "../utils/users/individualPermissions";
import { permissionChangesToAccess, splitAccessChanges } from "../utils/users/accessChanges";
import { hasWelfareReferentRight } from "../utils/users/welfareReferent";
import { readUserRole } from "../utils/users/userRole";
import { invalidatePrefix } from "../utils/cache/refDataCache";
import {
  chooseActivePark,
  overlayProfileForPark,
  readStoredActivePark,
  resolveParkAccess,
  writeStoredActivePark,
} from "../utils/users/activePark";
import { rememberTabPark } from "../utils/users/activeParkSwitch";
import { applyStaffAccessChanges } from "../utils/users/staffAccessChanges";
import { loadMultiParkAccess, setMultiParkEntry } from "../utils/users/multiParkAccess";
import { SECTOR_MEMBER_FIELDS } from "../utils/customParameters/parameterVisibility";

// La liste des utilisateurs du parc est servie depuis le cache de référentiel
// (`users:<zoo>`, 60 s) par la fiche secteur V2 et les cartes de tâches : toute
// mutation de profil ou de droits la vide.
const invalidateUsersListCache = () => invalidatePrefix("users:");

const db = initDb();

// Registre des droits par parc : rôle, permissions et parc d'un compte non
// AKONGO ne s'écrivent plus dans la fiche mais par commande serveur, qui tient
// aussi la fiche à jour pour les anciennes applications.
// Contrat : .scratch/droits-multi-parcs/contrat-registre.md
const callAccessCommand = async (name, data) => {
  const cloud = await initFunctions();
  const result = await cloud.httpsCallable(name)(data);
  return result?.data;
};

const currentPermissions = (profile = {}) => {
  const rights = profile.rights && typeof profile.rights === "object" ? profile.rights
    : profile.userRights && typeof profile.userRights === "object" ? profile.userRights : {};
  return {
    vet: rights.vet === true,
    technical: rights.technical === true,
    zimsPush: profile.canPushToZims === true,
    bea: hasWelfareReferentRight(profile),
  };
};

export const applyAccessChanges = async ({ uid, current, access, targetZooId, call = callAccessCommand }) => {
  if (!access && !targetZooId) return;
  if (targetZooId) {
    // Déplacer un compte (staff seulement côté serveur) : en une commande, le
    // nouveau parc devient son seul parc, avec ses droits. Ouvrir puis retirer
    // le ferait passer par deux parcs, réservés aux admins (étape 4, D5).
    await call("accessMoveAccount", {
      uid,
      zooId: targetZooId,
      role: access?.role || readUserRole(current.userRight),
      ...(access?.roleLabel !== undefined ? { roleLabel: access.roleLabel } : {}),
      ...(access?.roleFlags ? { roleFlags: access.roleFlags } : {}),
      permissions: { ...currentPermissions(current), ...(access?.permissions || {}) },
    });
    return;
  }
  await call("accessSetZooEntry", { uid, zooId: current.zooId, ...access });
};

// « Accès staff » d'un parc : une commande par membre AKONGO ajouté ou retiré
// (la liste est écrite par le serveur, copie des registres du staff).
export const saveStaffAccess = ({ zooId, before, after }) =>
  applyStaffAccessChanges({ zooId, before, after, call: callAccessCommand });

// Un membre du staff s'ajoute à un parc qui n'est pas dans son registre : même
// commande, sur lui-même, journalisée ; le serveur reprend le rôle de sa fiche.
export const addSelfToPark = ({ uid, zooId }) => callAccessCommand("accessSetZooEntry", { uid, zooId });

// « Accès aux autres parcs » d'une personne (étape 4) : les parcs multi-accès et
// l'état de la personne dans chacun, pour le staff AKONGO seulement (décision du
// 03/10) ; rien pour soi-même ni pour un autre lecteur.
export const loadOtherParksAccess = async (targetUid) => {
  const viewerUid = initAuth().currentUser?.uid;
  if (!viewerUid || !targetUid || viewerUid === targetUid) return { parks: [] };
  const viewer = (await new UserDatabaseConnector()._getUserData())?.data || {};
  return loadMultiParkAccess({ db, viewer, targetUid });
};

// Ouvre (rôle admin) ou retire un de ces parcs à la personne, par commande serveur.
export const setPersonParkAccess = ({ uid, zooId, enabled }) =>
  setMultiParkEntry({ call: callAccessCommand, targetUid: uid, zooId, enabled });

// Échec d'invitation lisible par l'interface : `code` du serveur et étape
// (`createUser` : aucun compte créé ; `sendInvitation` : le compte en attente
// existe, seul l'envoi a échoué — le recréer tomberait sur « adresse déjà utilisée »).
export const invitationFailure = (stage, cause, fallbackCode) => {
  // Le callable `createUser` renvoie l'erreur Admin SDK sérialisée : `{ errorInfo: { code, message } }`.
  const code = cause?.errorInfo?.code || cause?.code;
  const error = new Error(cause?.errorInfo?.message || cause?.message || fallbackCode);
  error.code = typeof code === "string" && code ? code : fallbackCode;
  error.stage = stage;
  if (cause) error.cause = cause;
  return error;
};

const LOCALSTORAGE_USERDATA = "userData";

// Entrée de l'index des membres d'un parc (zoo/{parc}/members/{uid}) lue comme
// une décision d'accès : rôle et droits de la personne dans CE parc.
const memberDecision = (entry) => ({
  admitted: true,
  staff: false,
  role: entry.role,
  roleLabel: entry.roleLabel ?? null,
  roleFlags: entry.roleFlags || {},
  permissions: entry.permissions || {},
  source: "grant",
});
const FakeSiteGen = new FakeSiteGenerator();

export class UserDatabaseConnector {
  async _getUsersList(zooId, concatAkongoAccess, withDeleted) {
    try {
      if (zooId == undefined) {
        zooId = await getZooId();
      }

      let userList = [];
      // Membres rattachés (fiche) et invités du parc (index des membres, étape 4
      // des droits multi-parcs : parc de rattachement ailleurs), lus ensemble.
      const [snapshot, guests] = await Promise.all([
        db.collection("users").where("zooId", "==", zooId).get(),
        this._getParkGuests(zooId),
      ]);
      snapshot.forEach((doc) => {
        let data = doc.data();
        if (data.isDeleted) {
          if (withDeleted) {
            userList.push({
              label: `${data.userFirstname} ${data.userLastname}`,
              value: data.userId,
              firstName: data.userFirstname,
              lastName: data.userLastname,
              email: data.userMail,
              ...data,
            });
          }
        } else {
          if (data.userId) {
            userList.push({
              label: `${data.userFirstname} ${data.userLastname}`,
              value: data.userId,
              firstName: data.userFirstname,
              lastName: data.userLastname,
              email: data.userMail,
              ...data,
            });
          }
        }
      });

      if (zooId !== "akongo") {
        userList = userList.filter((el) => !el.akongoStaff);
      }

      // Invités : leur fiche, avec le rôle et les droits de CE parc (sélecteurs
      // d'attribution, droits vétérinaire ou technique).
      for (const guest of guests) {
        if (!withDeleted && !isActiveUser(guest)) continue;
        userList.push({
          label: `${guest.userFirstname} ${guest.userLastname}`,
          value: guest.userId,
          firstName: guest.userFirstname,
          lastName: guest.userLastname,
          email: guest.userMail,
          ...guest,
        });
      }

      if (concatAkongoAccess) {
        try {
          let zooData = await (
            await db.collection("zoo").doc(zooId).get()
          ).data();
          let { akongoStaff } = await this._getAkongoStaff();
          zooData.staffAccess.forEach((userId) => {
            let user = _.find(akongoStaff, (e) => e.userId === userId);
            // `staffAccess` est une liste d'IDs figée dans le doc zoo : elle
            // n'est pas purgée quand un membre AKONGO quitte la boîte. Sans ce
            // garde, un ID orphelin faisait throw sur `user.userFirstname` et
            // le catch plus bas avalait TOUT le bloc AKONGO (donc plus aucun
            // membre AKONGO dans la liste, pas seulement le sortant).
            if (!user) return;
            // Les users du zoo sont déjà filtrés plus haut ; on applique la
            // même règle aux membres AKONGO, sinon un compte fermé continuait
            // d'apparaître dans les pickers d'attribution de campagne.
            if (!withDeleted && !isActiveUser(user)) return;
            userList.push({
              ...user,
              label: `AKONGO - ${user.userFirstname} ${user.userLastname}`,
              value: user.userId,
            });
          });
        } catch (error) {}
      }

      // debuplicate user list
      userList = _.uniqBy(userList, "userId");

      // sort by name
      userList = _.sortBy(userList, "label");

      return userList;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Invités d'un parc (index zoo/{parc}/members, entrées actives d'une personne
   * rattachée ailleurs), hors comptes AKONGO, que « Accès staff » couvre. Fiche
   * de chacun, avec le rôle et les droits de ce parc. Index absent ou illisible :
   * aucun invité, comme avant l'étape 4.
   */
  async _getParkGuests(zooId) {
    try {
      const snap = await db.collection("zoo").doc(zooId).collection("members")
        .where("guest", "==", true).where("status", "==", "active").get();
      const entries = [];
      snap.forEach((doc) => {
        const entry = doc.data();
        if (entry && entry.akongo !== true) entries.push({ uid: doc.id, ...entry });
      });
      if (!entries.length) return [];
      const profiles = await this._getUsersByUserIds(entries.map((entry) => entry.uid));
      const byId = new Map((profiles || []).map((profile) => [profile.userId, profile]));
      return entries
        .filter((entry) => byId.has(entry.uid))
        .map((entry) => {
          const profile = byId.get(entry.uid);
          const forPark = overlayProfileForPark(profile, { zooId, decision: memberDecision(entry) });
          return { ...profile, userRight: forPark.userRight, rights: forPark.rights, canPushToZims: forPark.canPushToZims, guest: true };
        });
    } catch (_error) {
      return [];
    }
  }

  // Résolution ciblée d'utilisateurs par userId (uid auth), quel que soit leur
  // zoo ou leur statut (staff AKONGO, supprimé...). Utilisé par l'historique de
  // modifications de campagne : l'auteur d'un edit n'est pas forcément dans la
  // liste des utilisateurs du zoo (work item #960).
  async _getUsersByUserIds(userIds) {
    try {
      const ids = _.uniq((userIds || []).filter(Boolean));
      if (ids.length === 0) return [];
      const chunks = _.chunk(ids, 10); // limite Firestore du "in"
      const results = [];
      await Promise.all(
        chunks.map(async (chunk) => {
          const snapshot = await db
            .collection("users")
            .where("userId", "in", chunk)
            .get();
          snapshot.forEach((doc) => results.push(doc.data()));
        })
      );
      return results;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async _updateUserEula(userId) {
    try {
      await db.collection("users").doc(userId).update({
        eula: eulaVersion,
      });
      return {
        success: true,
      };
    } catch (error) {
      // Le code Firestore distingue un refus des règles d'une panne réseau.
      return {
        success: false,
        code: error?.code || null,
      };
    }
  }

  async _updateUserLanguage(language) {
    try {
      const user = await this._getConnectedUser();
      if (user?.success && user?.data?.uid) {
        await db.collection("users").doc(user.data.uid).update({
          userMainLanguage: language,
        });
        return { success: true };
      }
      return { success: false, error: "No connected user" };
    } catch (error) {
      console.error("Error updating user language:", error);
      return { success: false, error };
    }
  }

  async _updateWebLanguagePreference(language) {
    try {
      const user = await this._getConnectedUser();
      if (user?.success && user?.data?.uid) {
        await db.collection("users").doc(user.data.uid).update({
          webLanguagePreference: language,
        });
        return { success: true };
      }
      return { success: false, error: "No connected user" };
    } catch (error) {
      console.error("Error updating web language preference:", error);
      return { success: false, error };
    }
  }

  async _getUserDetails(user) {
    let res;
    if (_.has(user, "userId")) {
      res = await db.collection("users").doc(user.userId).get();
    } else {
      res = await db.collection("users").doc(user).get();
    }
    return res.data();
  }

  // Le compte est créé avant l'envoi : un destinataire bloqué chez Postmark
  // (`skipped`, cf. recipientGuard) n'est pas un échec de création, sinon
  // l'admin retente et tombe sur « email déjà utilisé ». Un vrai échec d'envoi
  // reste une erreur.
  _invitationResult(delivery) {
    const skipped = delivery?.skipped === true;
    if (skipped) console.warn("[invitation] compte créé, email non envoyé :", delivery.reason);
    const invitationCode = typeof delivery?.code === "string" ? delivery.code : null;
    const success = delivery?.success === true || skipped;
    const result = { success, emailSkipped: skipped, invitationCode, data: delivery };
    return success ? result : { ...result, stage: "sendInvitation" };
  }

  async _setPendingUser(newUser) {
    try {
      let cloud = await initFunctions();
      newUser = { ...newUser };
      const invitationMode = newUser.invitationMode || "legacy";
      if (invitationMode === "code") {
        delete newUser.tempPassword;
      } else {
        // Also works with the historical createUser during the server rollout.
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        newUser.tempPassword = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
      }

      let createUser = cloud.httpsCallable("createUser");
      let snap = await createUser({ ...newUser });
      let res = snap.data;

      if (!res?.success) throw invitationFailure("createUser", res?.data, "USER_CREATION_FAILED");
      newUser = res.data;

      try {
        if (invitationMode === "code" && !newUser.invitationActivation) throw new Error("INVITATION_UNAVAILABLE");
        let localUser = await this._getUserData();
        let sendUserInvitation = cloud.httpsCallable("sendUserInvitation");

        if (readUserRole(localUser.data.userRight) === "superAdmin") {
          let res2 = await sendUserInvitation({
            invitationMode,
            userData: newUser,
            zooId: newUser.userZoo.value,
            zooNameInviteFrom: "Akongo",
            peopleNameInviteFrom: localUser.data.userFirstname,
          });
          return this._invitationResult(res2.data);
        } else {
          let res2 = await sendUserInvitation({
            invitationMode,
            userData: newUser,
            zooId: newUser.userZoo.value,
            zooNameInviteFrom: newUser.userZoo.label,
            peopleNameInviteFrom: localUser.data.userFirstname,
          });
          return this._invitationResult(res2.data);
        }
      } catch (error) {
        // `sendUserInvitation` lève une HttpsError dont le message porte le code métier (INVITATION_FORBIDDEN…).
        const businessCode = /^[A-Z][A-Z_]+$/.test(error?.message || "") ? error.message : error?.code;
        throw invitationFailure("sendInvitation", { code: businessCode, message: error?.message }, "INVITATION_FAILED");
      }
    } catch (error) {
      console.log("[invitation]", error?.stage, error?.code, error);
      return {
        success: false,
        stage: error?.stage || "createUser",
        data: error,
      };
    }
  }

  async storeUserDetails(user) {
    try {
      const userData = await this._persistSessionProfile(await this._getUserDetails(user.user.uid), user.user.uid);

      // Chaîne sur les comptes anciens, objet `{ value }` sur les récents :
      // `userRight.value` seul écrivait `{}` pour un compte ancien, qui perdait
      // son rôle dans tous les lecteurs de `_getUserRight`.
      await localStorage.setItem(
        "userRight",
        JSON.stringify({
          userRight: readUserRole(userData.userRight),
        })
      );

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  /**
   * Secteurs auxquels appartient l'utilisateur : présence dans n'importe quelle
   * liste de rôles du secteur (`SECTOR_MEMBER_FIELDS` : soigneur, gestion,
   * vétérinaire, nutrition, technique, zoologie — retours ICM du 12/10, ticket
   * 02, même définition que le mobile). Mis en cache dans `userData.sectorsIds`
   * pour permettre au filtrage synchrone de visibilité des paramètres
   * personnalisés de vérifier le partage par secteur (cf.
   * utils/customParameters/parameterVisibility).
   */
  async _fetchUserSectorIds(userId, zooId) {
    try {
      if (!userId || !zooId) return [];
      const db = initDb();
      // Une requête par liste de rôles, chacune à un seul filtre (index
      // automatique) : zooId + array-contains exigerait un index composite
      // absent, la requête échouait et renvoyait [] (SAN-22). Une liste en
      // erreur n'efface pas les secteurs trouvés par les autres.
      const snaps = await Promise.all(
        SECTOR_MEMBER_FIELDS.map((field) =>
          db
            .collection("sectors")
            .where(field, "array-contains", userId)
            .get()
            .catch((error) => {
              console.log("_fetchUserSectorIds error", field, error);
              return null;
            })
        )
      );
      const ids = [];
      snaps.forEach((snap) => {
        if (!snap) return;
        snap.forEach((doc) => {
          const d = doc.data();
          if (d.zooId && d.zooId !== zooId) return;
          const id = d.sectorId || doc.id;
          if (id && !ids.includes(id)) ids.push(id);
        });
      });
      return ids;
    } catch (e) {
      console.log("_fetchUserSectorIds error", e);
      return [];
    }
  }

  async _connectUser(login, password, rememberMe = true) {
    try {
      let auth = await initAuth();

      // Définir la persistence selon le choix de l'utilisateur
      // LOCAL = persiste après fermeture navigateur (Remember Me coché)
      // SESSION = session uniquement (Remember Me décoché)
      const persistence = rememberMe
        ? AuthPersistence.LOCAL
        : AuthPersistence.SESSION;

      await auth.setPersistence(persistence);
      let user = await auth.signInWithEmailAndPassword(login, password);

      await this.storeUserDetails(user);
      return {
        success: true,
        data: user,
      };
    } catch (error) {
      console.log("Erreur de connexion");
      console.log(error);
      return {
        success: false,
        data: error,
      };
    }
  }

  /**
   * Connexion via Microsoft OAuth
   * @param {string} tenantId - Optionnel: Azure AD Tenant ID pour restreindre la connexion
   * @returns {Object} { success: boolean, data: user | error }
   */
  async _connectWithMicrosoft(tenantId = null) {
    try {
      let auth = await initAuth();

      // Utiliser LOCAL persistence pour OAuth (session persiste après fermeture navigateur)
      await auth.setPersistence(AuthPersistence.LOCAL);

      const provider = new firebase.auth.OAuthProvider("microsoft.com");

      // Si un tenantId est fourni, restreindre au tenant Azure AD
      if (tenantId) {
        provider.setCustomParameters({
          tenant: tenantId,
        });
      }

      // Ajouter les scopes nécessaires
      provider.addScope("email");
      provider.addScope("profile");

      const result = await auth.signInWithPopup(provider);
      await this.storeUserDetails(result);

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      console.log("Erreur de connexion Microsoft:", error);
      return {
        success: false,
        data: error,
      };
    }
  }

  /**
   * Connexion via Google OAuth
   * @returns {Object} { success: boolean, data: user | error }
   */
  async _connectWithGoogle() {
    try {
      let auth = await initAuth();

      await auth.setPersistence(AuthPersistence.LOCAL);

      const provider = new firebase.auth.OAuthProvider("google.com");
      provider.addScope("email");
      provider.addScope("profile");

      const result = await auth.signInWithPopup(provider);
      await this.storeUserDetails(result);

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      console.log("Erreur de connexion Google:", error);
      return {
        success: false,
        data: error,
      };
    }
  }

  /**
   * Connexion via OpenID Connect (Okta, Auth0, Keycloak, etc.)
   * @param {string} providerId - L'identifiant du provider OIDC configuré dans Firebase (ex: "oidc.okta")
   * @param {string[]} scopes - Scopes additionnels à demander
   * @returns {Object} { success: boolean, data: user | error }
   */
  async _connectWithOIDC(providerId = "oidc.okta", scopes = []) {
    try {
      let auth = await initAuth();

      await auth.setPersistence(AuthPersistence.LOCAL);

      const provider = new firebase.auth.OAuthProvider(providerId);

      // Scopes OpenID standard
      provider.addScope("openid");
      provider.addScope("email");
      provider.addScope("profile");
      // Scopes additionnels configurés par l'admin
      for (const scope of scopes) {
        provider.addScope(scope);
      }

      const result = await auth.signInWithPopup(provider);
      await this.storeUserDetails(result);

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      console.log("Erreur de connexion OIDC:", error);
      return {
        success: false,
        data: error,
      };
    }
  }

  /**
   * Récupère la configuration d'authentification d'un zoo
   * @param {string} zooId - L'identifiant du zoo
   * @returns {Object} La configuration auth du zoo ou les valeurs par défaut
   */
  async _getZooAuthConfig(zooId) {
    try {
      const db = initDb();
      const zooDoc = await db.collection("zoo").doc(zooId).get();
      const zooData = zooDoc.data();

      // Retourner la config auth ou les valeurs par défaut
      return (
        zooData?.authConfig || {
          enabledProviders: {
            emailPassword: true,
            microsoft: false,
            google: false,
            oidc: false,
          },
          microsoftConfig: {
            tenantId: "",
          },
          oidcConfig: {
            providerId: "",
            displayName: "",
            issuerUrl: "",
            clientId: "",
            scopes: "",
          },
          ssoProvider: "microsoft",
          mfaRequired: false,
        }
      );
    } catch (error) {
      console.log("Erreur lors de la récupération de la config auth:", error);
      return {
        enabledProviders: {
          emailPassword: true,
          microsoft: false,
          google: false,
          oidc: false,
        },
      };
    }
  }

  /**
   * Vérifie si un email correspond à un domaine SSO obligatoire
   * Utilise la collection publique ssoDomains (lecture sans auth, pas de data sensible)
   * @param {string} email - L'email de l'utilisateur
   * @returns {Object} { ssoRequired: boolean, provider: string|null, tenantId: string|null }
   */
  async _checkSSOByEmail(email) {
    try {
      const domain = email.split("@")[1]?.toLowerCase();
      if (!domain) return { ssoRequired: false };

      const db = initDb();
      const domainDoc = await db.collection("ssoDomains").doc(domain).get();

      if (!domainDoc.exists) return { ssoRequired: false };

      const config = domainDoc.data();
      return {
        ssoRequired: true,
        provider: config.provider || "microsoft",
        tenantId: config.tenantId || null,
        mode: config.mode || "all",
        oidcProviderId: config.oidcProviderId || null,
        oidcScopes: config.oidcScopes || [],
      };
    } catch (error) {
      console.log("Erreur lors de la vérification SSO par domaine:", error);
      return { ssoRequired: false };
    }
  }

  /**
   * Enregistre un domaine email comme SSO obligatoire
   * À appeler depuis l'admin (ZooDetails) lors de la config SSO
   * @param {string} domain - Le domaine email (ex: "company.com")
   * @param {Object} config - { zooId, provider, tenantId }
   */
  async _setSSODomain(domain, config) {
    try {
      const db = initDb();
      await db.collection("ssoDomains").doc(domain.toLowerCase()).set({
        zooId: config.zooId,
        provider: config.provider || "microsoft",
        tenantId: config.tenantId || null,
        updatedAt: new Date(),
      });
      return { success: true };
    } catch (error) {
      console.log("Erreur lors de l'enregistrement du domaine SSO:", error);
      return { success: false, error };
    }
  }

  /**
   * Supprime un domaine SSO
   * @param {string} domain - Le domaine email à supprimer
   */
  async _removeSSODomain(domain) {
    try {
      const db = initDb();
      await db.collection("ssoDomains").doc(domain.toLowerCase()).delete();
      return { success: true };
    } catch (error) {
      console.log("Erreur lors de la suppression du domaine SSO:", error);
      return { success: false, error };
    }
  }

  async _getConnectedUser() {
    let auth = await initAuth();
    var user = await auth.currentUser;

    if (user) {
      return {
        success: true,
        data: user,
      };
    } else {
      return {
        success: false,
      };
    }
  }

  /**
   * Écoute les changements d'état d'authentification Firebase
   * Utilisé pour l'auto-login quand une session est persistée
   * @param {Function} callback - Fonction appelée avec l'utilisateur (ou null)
   * @returns {Function} Fonction pour se désabonner du listener
   */
  async _onAuthStateChanged(callback) {
    let auth = await initAuth();
    return auth.onAuthStateChanged(callback);
  }

  /**
   * Attend que l'état d'authentification Firebase soit déterminé
   * Utilisé pour l'ouverture d'URL en nouvel onglet où auth.currentUser peut être null
   * avant que Firebase ait restauré la session persistée
   * @param {number} timeout - Timeout en ms (défaut: 5000)
   * @returns {Object} { success: boolean, data?: user }
   */
  async _waitForAuthState(timeout = 5000) {
    let auth = await initAuth();

    // Si l'utilisateur est déjà déterminé, retourner immédiatement
    if (auth.currentUser) {
      return { success: true, data: auth.currentUser };
    }

    return new Promise((resolve) => {
      let resolved = false;

      // Timer de timeout
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          unsubscribe();
          resolve({ success: false });
        }
      }, timeout);

      // Écouter le changement d'état d'auth
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          unsubscribe();
          if (user) {
            resolve({ success: true, data: user });
          } else {
            resolve({ success: false });
          }
        }
      });
    });
  }

  /**
   * Récupère l'utilisateur actuellement connecté (synchrone après init)
   * @returns {Object|null} L'utilisateur Firebase ou null
   */
  async _getCurrentUser() {
    let auth = await initAuth();
    return auth.currentUser;
  }

  // ============================================================
  // Reauthentication Methods
  // ============================================================

  /**
   * Ré-authentifie l'utilisateur avec son mot de passe
   * Nécessaire avant certaines opérations sensibles (MFA, changement email, etc.)
   * @param {string} password - Le mot de passe actuel de l'utilisateur
   * @returns {Object} { success: boolean, error?: string }
   */
  async _reauthenticateWithPassword(password) {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      const credential = firebase.auth.EmailAuthProvider.credential(
        user.email,
        password
      );

      await user.reauthenticateWithCredential(credential);

      return { success: true };
    } catch (error) {
      console.error("Erreur ré-authentification:", error);
      return {
        success: false,
        error: error.code || error.message || error,
      };
    }
  }

  // ============================================================
  // Email Verification Methods
  // ============================================================

  /**
   * Vérifie si l'email de l'utilisateur est vérifié
   * @returns {Object} { verified: boolean, email: string }
   */
  async _checkEmailVerificationStatus() {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) return { verified: false, email: null };

      // Recharger l'utilisateur pour avoir les dernières infos
      await user.reload();

      return {
        verified: user.emailVerified,
        email: user.email,
      };
    } catch (error) {
      console.error("Erreur vérification email:", error);
      return { verified: false, email: null };
    }
  }

  /**
   * Envoie un email de vérification à l'utilisateur
   * @returns {Object} { success: boolean, error?: string }
   */
  async _sendEmailVerification() {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      await user.sendEmailVerification();

      return { success: true };
    } catch (error) {
      console.error("Erreur envoi email de vérification:", error);
      return {
        success: false,
        error: error.code || error.message || error,
      };
    }
  }

  /**
   * Recharge l'utilisateur Firebase pour récupérer les dernières infos
   * (utile après vérification d'email)
   * @returns {Object} { success: boolean, emailVerified: boolean }
   */
  async _reloadUser() {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) return { success: false, emailVerified: false };

      await user.reload();

      return {
        success: true,
        emailVerified: user.emailVerified,
      };
    } catch (error) {
      console.error("Erreur rechargement utilisateur:", error);
      return { success: false, emailVerified: false };
    }
  }

  // ============================================================
  // MFA TOTP Methods
  // ============================================================

  /**
   * Vérifie si l'utilisateur a MFA activé
   * @returns {Object} { enrolled: boolean, factors: Array }
   */
  async _checkUserMFAStatus() {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) return { enrolled: false, factors: [] };

      const enrolledFactors = user.multiFactor?.enrolledFactors || [];
      return {
        enrolled: enrolledFactors.length > 0,
        factors: enrolledFactors.map((f) => ({
          uid: f.uid,
          displayName: f.displayName,
          factorId: f.factorId,
          enrollmentTime: f.enrollmentTime,
        })),
      };
    } catch (error) {
      console.error("Erreur vérification MFA:", error);
      return { enrolled: false, factors: [] };
    }
  }

  /**
   * Démarre l'enrollment TOTP - Génère le secret et QR code
   * @returns {Object} { secret, qrCodeUrl, secretKey }
   */
  async _startTOTPEnrollment() {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      // Obtenir une session MFA
      const session = await user.multiFactor.getSession();

      // Générer le secret TOTP
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);

      // Générer l'URL du QR code
      const qrCodeUrl = totpSecret.generateQrCodeUrl(
        user.email,
        "Akongo Animal Welfare Monitor"
      );

      return {
        success: true,
        secret: totpSecret,
        qrCodeUrl: qrCodeUrl,
        secretKey: totpSecret.secretKey,
      };
    } catch (error) {
      console.error("Erreur démarrage enrollment TOTP:", error);
      return {
        success: false,
        error: error.message || error,
      };
    }
  }

  /**
   * Finalise l'enrollment TOTP avec le code de vérification
   * @param {Object} totpSecret - Le secret TOTP retourné par _startTOTPEnrollment
   * @param {string} verificationCode - Le code à 6 chiffres de l'app authenticator
   * @param {string} displayName - Nom du facteur (optionnel)
   * @returns {Object} { success: boolean }
   */
  async _completeTOTPEnrollment(
    totpSecret,
    verificationCode,
    displayName = "Authenticator"
  ) {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      // Créer l'assertion avec le code
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode
      );

      // Enroller le facteur
      await user.multiFactor.enroll(assertion, displayName);

      return { success: true };
    } catch (error) {
      console.error("Erreur finalisation enrollment TOTP:", error);
      return {
        success: false,
        error: error.code || error.message || error,
      };
    }
  }

  /**
   * Désactive MFA pour l'utilisateur
   * @param {string} factorUid - L'UID du facteur à supprimer
   * @returns {Object} { success: boolean }
   */
  async _unenrollMFA(factorUid) {
    try {
      const auth = initAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Utilisateur non connecté");

      const factor = user.multiFactor.enrolledFactors.find(
        (f) => f.uid === factorUid
      );
      if (!factor) throw new Error("Facteur non trouvé");

      await user.multiFactor.unenroll(factor);
      return { success: true };
    } catch (error) {
      console.error("Erreur désactivation MFA:", error);
      return {
        success: false,
        error: error.message || error,
      };
    }
  }

  /**
   * Résout le challenge MFA pendant le login
   * @param {Object} error - L'erreur Firebase contenant le resolver
   * @param {string} verificationCode - Le code TOTP à 6 chiffres
   * @returns {Object} UserCredential ou erreur
   */
  async _resolveMFAChallenge(error, verificationCode) {
    try {
      const auth = initAuth();
      const resolver = getMultiFactorResolver(auth, error);

      // Trouver le facteur TOTP
      const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );

      if (!totpHint) throw new Error("Aucun facteur TOTP trouvé");

      // Créer l'assertion de vérification
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        verificationCode
      );

      // Résoudre le sign-in
      const userCredential = await resolver.resolveSignIn(assertion);

      // Stocker les données utilisateur
      await this.storeUserDetails(userCredential);

      return {
        success: true,
        data: userCredential,
      };
    } catch (error) {
      console.error("Erreur résolution challenge MFA:", error);
      return {
        success: false,
        error: error.code || error.message || error,
      };
    }
  }

  // ============================================================
  // End MFA TOTP Methods
  // ============================================================

  async handleChangeRight(userId, newRight) {
    await db.collection("users").doc(userId).update({
      userRight: newRight,
      ...getModificationStamp(),
    });
  }

  async deleteAccount(idOfWhoDeleted, userId) {
    await db.collection("users").doc(userId).update({
      isDeleted: true,
      deletedBy: idOfWhoDeleted,
      ...getModificationStamp(),
    });
  }

  async handleEndOfPending(user) {
    await db.collection("users").doc(user.uid).update({
      tempPassword: null,
      userStatus: "active",
    });
  }

  async _updateUserPassword(login, prevPassword, newPassword) {
    try {
      let res = await this._connectUser(login, prevPassword);
      if (res.success) {
        let res2 = await this._getConnectedUser();
        let user = res2.data;
        await user.updatePassword(newPassword);
        await this.handleEndOfPending(user);
        return {
          success: true,
          data: user,
        };
      } else {
        return {
          success: false,
          data: res.data,
        };
      }
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  async _handleForgottenPassword(login) {
    try {
      // Envoi via Cloud Function (lien Firebase + email Postmark stylé)
      let cloud = await initFunctions();
      let sendPasswordResetEmail = cloud.httpsCallable(
        "sendPasswordResetEmail"
      );
      await sendPasswordResetEmail({ email: login });
      return {
        success: true,
      };
    } catch (error) {
      console.log("Erreur lors de l'envoi de l'email de reset:", error);
      // Fallback : email standard Firebase si la function est indisponible
      try {
        let auth = await initAuth();
        await auth.sendPasswordResetEmail(login);
        return {
          success: true,
        };
      } catch (fallbackError) {
        return {
          success: false,
        };
      }
    }
  }

  async _disconnectUser() {
    try {
      let auth = await initAuth();
      await auth.signOut();

      // Nettoyer toutes les données sensibles du localStorage
      localStorage.removeItem("tempZooTier");
      localStorage.removeItem("userData");
      localStorage.removeItem("userRight");
      // Supprimer les anciennes clés du Remember Me (migration)
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("rememberMeCrypt");

      return { success: true };
    } catch (error) {
      console.log(error);
      return { success: false, error };
    }
  }

  /**
   * Nettoie les anciennes clés Remember Me du localStorage
   * Conservée pour rétrocompatibilité avec Header.js
   */
  async _RemoveRememberMe() {
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("rememberMeCrypt");
  }

  async _getEulaStatus(userId) {
    let user = await this._getUserDetails(userId);
    if (_.has(user, "eula")) {
      if (user.eula === eulaVersion) {
        return true;
      }
    }
    return false;
  }

  /**
   * Rôle de l'utilisateur connecté, depuis le cache local.
   *
   * La clé `userRight` n'est écrite qu'au login (storeUserDetails). Quand une
   * session Firebase persistée est restaurée sans elle (localStorage partiel,
   * auto-login sans nouveau storeUserDetails), la Sidebar lisait `res.data`
   * undefined, levait, et renvoyait sur /login, qui relançait l'auto-login :
   * boucle infinie (constatée le 2026-09-03 en local sur un compte staff).
   * On retombe donc sur `userData.userRight` (même origine, même login) et on
   * ré-écrit la clé pour les lecteurs suivants.
   *
   * `data.userRight` est toujours une chaîne : les lecteurs le comparent
   * directement (`=== "superAdmin"`). Une clé sans rôle lisible (`{}` écrit
   * pour les comptes à rôle chaîne avant le 23/09/2026) est réparée depuis
   * `userData` au lieu d'être servie telle quelle.
   */
  async _getUserRight() {
    let cached;
    let hasCache = false;
    const stored = localStorage.getItem("userRight");
    if (stored !== null) {
      try {
        cached = JSON.parse(stored);
        hasCache = true;
      } catch (error) {
        localStorage.removeItem("userRight");
      }
    }
    const cachedRole = readUserRole(cached?.userRight);
    if (cachedRole) {
      return { success: true, data: { ...cached, userRight: cachedRole } };
    }
    try {
      const userData = JSON.parse(localStorage.getItem(LOCALSTORAGE_USERDATA) || "null");
      const value = readUserRole(userData?.userRight);
      if (value) {
        const data = { userRight: value };
        localStorage.setItem("userRight", JSON.stringify(data));
        return { success: true, data };
      }
    } catch (error) {
      console.warn("[_getUserRight] cache userData illisible", error);
    }
    if (hasCache) {
      return { success: true, data: cached };
    }
    return {
      success: false,
    };
  }

  async _getZooData(explicitZooId) {
    try {
      // Protected V2 entry points pass the zoo id read from users/{auth.uid}.
      // Legacy callers may still omit it while their migration is in progress.
      const zooId = typeof explicitZooId === "string" && explicitZooId.trim()
        ? explicitZooId.trim()
        : await getZooId();
      if (!zooId) {
        return {
          success: false,
          data: null,
        };
      }

      // Récupérer les données du zoo depuis Firestore
      const zooDoc = await db.collection("zoo").doc(zooId).get();

      if (!zooDoc.exists) {
        console.warn(`Zoo document not found for ID: ${zooId}`);
        return {
          success: false,
          data: null,
        };
      }

      const zooData = zooDoc.data();

      // Retourner les données du zoo incluant le niveau de service
      // Le champ dans Firestore est 'zooTiers' qui contient un objet {value: "discovery|essential|progress|fullcare|research_partner"}
      let followUpLevel =
        zooData.zooTiers?.value || zooData.zooTiers || "essential";

      // Mapper les valeurs legacy
      if (followUpLevel === "basic") {
        console.log('Mapping legacy "basic" tier to "essential"');
        followUpLevel = "essential";
      }

      console.log("Extracted followUpLevel:", followUpLevel);

      return {
        success: true,
        data: {
          ...zooData,
          zooId,
          followUpLevel: followUpLevel, // Extraire la valeur de zooTiers
        },
      };
    } catch (error) {
      console.error("Error fetching zoo data:", error);
      return {
        success: false,
        data: null,
        error: error.message,
      };
    }
  }

  /**
   * Registre des droits de la personne connectée (elle seule le lit) : ses
   * parcs, et son rôle et ses droits dans chacun. Une erreur de lecture n'est
   * jamais prise pour une absence (même règle que le serveur).
   */
  async _getOwnAccessGrant(uid) {
    try {
      const snap = await db.collection("accessGrants").doc(uid).get();
      return snap.exists ? { grant: snap.data(), grantState: "present" } : { grant: null, grantState: "absent" };
    } catch (_error) {
      return { grant: null, grantState: "unreadable" };
    }
  }

  /** Parc mémorisé par ce navigateur pour ce compte (étape 4), ou null. */
  _getStoredActivePark(uid) {
    return readStoredActivePark(localStorage, uid);
  }

  /** Ce parc peut-il être ouvert pour y travailler (registre, ou fiche avant migration) ? */
  async _canOpenPark(uid, zooId) {
    const user = (await db.collection("users").doc(uid).get()).data();
    const { grant, grantState } = await this._getOwnAccessGrant(uid);
    return resolveParkAccess({ user, grant, grantState, zooId }).admitted;
  }

  /** Entrée active d'une personne dans l'index des membres d'un parc, ou null. */
  async _getMemberEntry(zooId, uid) {
    try {
      const snap = await db.collection("zoo").doc(zooId).collection("members").doc(uid).get();
      const entry = snap.exists ? snap.data() : null;
      return entry && entry.status === "active" ? entry : null;
    } catch (_error) {
      return null;
    }
  }

  /**
   * Fiche d'une personne vue depuis un parc : pour une invitée de ce parc (ou
   * un autre de ses parcs), rôle et droits de ce parc, tels que l'admin les règle.
   */
  async _getUserProfileForPark(userId, zooId) {
    const doc = (await db.collection("users").doc(userId).get()).data();
    if (!doc) return { success: false };
    if (!zooId || zooId === doc.zooId) return { success: true, data: doc };
    const entry = await this._getMemberEntry(zooId, userId);
    if (!entry) return { success: true, data: doc };
    // `guest` et `homeZooId` : repères d'écran (données professionnelles gérées
    // par le parc de rattachement), jamais réécrits dans la fiche (_updateUserData).
    return {
      success: true,
      data: { ...overlayProfileForPark(doc, { zooId, decision: memberDecision(entry) }), guest: true, homeZooId: doc.zooId },
    };
  }

  /**
   * Seul point d'écriture de la copie locale du profil (`userData`), étape 4
   * (décision D6) : parc actif du navigateur, revérifié par le registre, avec le
   * rôle et les droits de ce parc et les secteurs de ce parc. Rend la copie.
   * Un parc mémorisé qui n'est plus autorisé cède la place au parc de la fiche.
   */
  async _persistSessionProfile(serverProfile, uid, extra = {}) {
    const { grant, grantState } = await this._getOwnAccessGrant(uid);
    const stored = readStoredActivePark(localStorage, uid);
    const zooId = chooseActivePark({ user: serverProfile, grant, grantState, stored }) || serverProfile?.zooId || null;
    const decision = zooId ? resolveParkAccess({ user: serverProfile, grant, grantState, zooId }) : null;
    let zooName = stored?.zooId === zooId ? stored.zooName : null;
    if (zooId && !zooName && zooId !== serverProfile?.zooId) {
      try {
        zooName = (await db.collection("zoo").doc(zooId).get()).data()?.zooName || null;
      } catch (_error) {
        zooName = null;
      }
    }
    const profile = zooId
      ? overlayProfileForPark(serverProfile || {}, { zooId, zooName, decision })
      : { ...(serverProfile || {}) };
    if (zooId && stored?.zooId !== zooId) {
      writeStoredActivePark(localStorage, { uid, zooId, zooName: profile.userZoo?.label || null });
    }
    const sectorsIds = await this._fetchUserSectorIds(profile.userId || uid, zooId);
    const snapshot = { ...profile, ...extra, sectorsIds };
    localStorage.setItem(LOCALSTORAGE_USERDATA, JSON.stringify(snapshot));
    rememberTabPark(zooId);
    return snapshot;
  }

  /**
   * Rafraîchit la copie locale après l'enregistrement de son propre profil :
   * relu sur le serveur, puis écrit par le seul point d'écriture (parc actif,
   * rôle et droits de ce parc). La préférence de langue propre au web est gardée.
   */
  async _updateTempUserData(userData) {
    try {
      let webLanguagePreference;
      try {
        webLanguagePreference = JSON.parse(localStorage.getItem(LOCALSTORAGE_USERDATA) || "null")?.webLanguagePreference;
      } catch (e) { /* ignore parse error */ }
      const uid = userData?.userId;
      const authUid = initAuth()?.currentUser?.uid;
      if (!uid || (authUid && authUid !== uid)) return { success: false, data: new Error("NOT_OWN_PROFILE") };
      const server = (await db.collection("users").doc(uid).get()).data() || userData;
      const snapshot = await this._persistSessionProfile(server, uid, webLanguagePreference ? { webLanguagePreference } : {});
      return {
        success: true,
        data: snapshot,
      };
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  async _updateUserRights(userId, zooId, changes) {
    invalidateUsersListCache();
    try {
      const current = (await initDb().collection("users").doc(userId).get()).data() || {};
      if (current.akongoStaff === true) {
        // Compte AKONGO : accès global hors registre, droits toujours portés par la fiche.
        await updateIndividualPermissions({ db: initDb(), userId, zooId, changes, stamp: getModificationStamp() });
        return { success: true };
      }
      const permissions = permissionChangesToAccess(changes);
      if (Object.keys(permissions).length) await callAccessCommand("accessSetZooEntry", { uid: userId, zooId, permissions });
      // Le résumé hebdomadaire ne suit pas les droits : chacun coche sa case
      // (décision du 29/10/2026, plus d'abonnement d'office).
      // Les données professionnelles vétérinaires restent une donnée de profil,
      // contrôlée au parc de la fiche (rattachement), même vue depuis le parc
      // d'une invitée : sinon PROFILE_ZOO_CHANGED après une commande réussie
      // (audit du 04/10). La règle `users` réserve cette écriture au parc de
      // rattachement, à la personne et au staff.
      if (changes.vetData) {
        await updateIndividualPermissions({ db: initDb(), userId, zooId: current.zooId, changes: { vetData: changes.vetData }, stamp: getModificationStamp() });
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.code || error.message, data: error };
    }
  }

  async _updateUserData(userData, adminEdit, prevZooID) {
    invalidateUsersListCache();
    let db = initDb();
    try {
      // `invitationActivation` et `termsAcceptance` appartiennent au serveur
      // (activation par code). Quand le profil vient du cache localStorage, leurs
      // Timestamps ont été sérialisés en {seconds, nanoseconds} : les réécrire
      // serait vu comme une modification et la règle `users` refuserait tout
      // l'update. On ne les renvoie jamais ; `update()` laisse les clés absentes
      // intactes.
      // `activeZooId` et `sectorsIds` n'existent que dans la copie locale (parc
      // actif, étape 4) : jamais dans la fiche. `guest` et `homeZooId` sont des
      // repères d'écran (_getUserProfileForPark).
      const { invitationActivation, termsAcceptance, activeZooId, sectorsIds, guest, homeZooId, ...editable } = userData;
      const ref = db.collection("users").doc(userData.userId);
      const current = (await ref.get()).data() || {};
      // Son propre profil ne déplace jamais la fiche vers le parc actif du navigateur.
      const ownEdit = adminEdit !== true;
      if (current.akongoStaff === true) {
        // Compte AKONGO : profil et rôle AKONGO dans la fiche ; seul un autre
        // membre du staff change son parc, explicitement.
        const { zooId: _zooId, userZoo, ...rest } = editable;
        userData = {
          ...rest,
          ...(ownEdit ? {} : { zooId: userData.userZoo.value, userZoo }),
          ...getModificationStamp(),
        };
        await ref.update(userData);
        userData = { ...(await ref.get()).data() };
      } else if (activeZooId && activeZooId !== current.zooId) {
        // L'écran montrait les droits d'un autre parc que le rattachement
        // (invitée de ce parc, ou soi-même dans un autre de ses parcs) : rôle et
        // droits vont à ce parc ; le profil garde le parc et le rôle de rattachement.
        const entry = await this._getMemberEntry(activeZooId, userData.userId);
        const currentForPark = entry
          ? overlayProfileForPark(current, { zooId: activeZooId, decision: memberDecision(entry) })
          : null;
        const { access } = currentForPark
          ? splitAccessChanges(currentForPark, { ...editable, zooId: activeZooId, userZoo: currentForPark.userZoo })
          : { access: null };
        if (access) await callAccessCommand("accessSetZooEntry", { uid: userData.userId, zooId: activeZooId, ...access });
        // La commande réécrit la copie des droits de la fiche : le profil se
        // calcule sur la fiche relue (voir la branche suivante).
        const fresh = access ? (await ref.get()).data() || {} : current;
        const { profile } = splitAccessChanges(fresh, { ...editable, zooId: fresh.zooId, userZoo: fresh.userZoo });
        await ref.update({ ...profile, ...getModificationStamp() });
        userData = { ...(await ref.get()).data() };
      } else {
        // Les droits passent par le registre (commande serveur, qui met aussi à
        // jour la fiche) ; le reste du profil s'écrit directement, sans jamais
        // renvoyer une copie périmée des droits.
        const next = ownEdit ? { ...editable, zooId: current.zooId, userZoo: current.userZoo } : editable;
        const { access, targetZooId } = splitAccessChanges(current, next);
        await applyAccessChanges({ uid: userData.userId, current, access, targetZooId });
        // Le profil se calcule sur la fiche relue après la commande : calculé
        // avant, il renvoyait l'ancien rôle, la règle `users` refusait l'écriture
        // et le reste du profil (prénom…) n'était pas enregistré (audit du 04/10).
        const fresh = access || targetZooId ? (await ref.get()).data() || {} : current;
        const { profile } = splitAccessChanges(fresh, { ...next, zooId: fresh.zooId, userZoo: fresh.userZoo });
        await ref.update({ ...profile, ...getModificationStamp() });
        userData = { ...(await ref.get()).data() };
      }

      // Ne JAMAIS écraser l'identité de la session locale (LOCALSTORAGE_USERDATA,
      // clé lue par _getUserData pour savoir « qui je suis ») lorsqu'on édite le
      // compte d'un TIERS (adminEdit === true). Sinon le manager/admin prend
      // l'identité de l'utilisateur qu'il vient de modifier.
      // En édition de son propre compte, on ne rafraîchit la session locale que
      // si le zoo a changé.
      if (adminEdit !== true && prevZooID !== userData.zooId) {
        await this._persistSessionProfile(userData, userData.userId);
      }

      return {
        success: true,
        data: userData,
      };
    } catch (error) {
      return {
        success: false,
        data: error,
      };
    }
  }

  async _getUserData(userId, online) {
    if (online) {
      let userData = await (
        await db.collection("users").doc(userId).get()
      ).data();
      // `userData` est l'identité de la session pour toute l'app : n'y écrire
      // que le profil de la personne connectée, jamais celui d'un tiers lu au
      // passage (sinon « Moi » et les droits changent au fil de la navigation).
      const authUid = initAuth()?.currentUser?.uid;
      if (!authUid || authUid === userId) {
        // Copie de session : parc actif (registre), rôle et droits de ce parc,
        // et secteurs dont la personne est soigneuse dans ce parc, lus par le
        // partage des paramètres personnalisés (viewerFromLocalStorage) : sans
        // eux, un paramètre partagé à son secteur restait invisible (recette
        // 12.1.0 SAN-22).
        return {
          data: await this._persistSessionProfile(userData, userId),
          success: true,
        };
      }
      return {
        data: userData,
        success: true,
      };
    }

    if (userId === undefined) {
      let userData = localStorage.getItem("userData");
      if (userData !== null) {
        return {
          success: true,
          data: JSON.parse(userData),
        };
      } else {
        return {
          success: false,
        };
      }
    } else {
      let userData = await (
        await db.collection("users").doc(userId).get()
      ).data();
      return {
        data: userData,
        success: true,
      };
    }
  }

  async _getAccessGrantedZooFromUser(userId) {
    let snap = await db
      .collection("zoo")
      .where("staffAccess", "array-contains", userId)
      .get();
    let zoo = [];
    snap.forEach((el) => {
      let data = el.data();
      data.label = data.zooName;
      data.value = data.zooId;
      zoo.push(data);
    });
    return zoo;
  }

  async _sendPasswordResetEmail(email) {
    try {
      let cloud = await initFunctions();
      let adminSendPasswordResetEmail = cloud.httpsCallable(
        "adminSendPasswordResetEmail"
      );

      let result = await adminSendPasswordResetEmail({
        userEmail: email,
      });

      return result.data;
    } catch (error) {
      console.log("Erreur lors de l'envoi de l'email de reset:", error);
      return {
        success: false,
        error: error,
      };
    }
  }

  async _adminUpdateUserPassword(userId, newPassword) {
    try {
      // Utiliser les fonctions cloud pour changer le mot de passe en tant qu'admin
      let cloud = await initFunctions();
      let adminUpdatePassword = cloud.httpsCallable("adminUpdateUserPassword");

      let result = await adminUpdatePassword({
        userId: userId,
        newPassword: newPassword,
      });

      if (result.data.success) {
        // Mettre à jour le statut de l'utilisateur pour indiquer que le mot de passe a été changé par un admin
        await db.collection("users").doc(userId).update({
          passwordChangedByAdmin: true,
          passwordChangedAt: new Date().getTime(),
          tempPassword: null,
          userStatus: "active",
        });

        return {
          success: true,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result.data.error,
        };
      }
    } catch (error) {
      console.log(
        "Erreur lors de la mise à jour du mot de passe admin:",
        error
      );

      // Fallback: essayer de mettre à jour directement dans Firestore
      // Note: Ceci ne changera pas le mot de passe Firebase Auth, seulement les métadonnées
      try {
        await db.collection("users").doc(userId).update({
          tempPassword: newPassword,
          passwordChangedByAdmin: true,
          passwordChangedAt: new Date().getTime(),
          userStatus: "pending", // L'utilisateur devra changer son mot de passe à la prochaine connexion
        });

        return {
          success: true,
          data: {
            message:
              "Mot de passe temporaire défini. L'utilisateur devra le changer à la prochaine connexion.",
          },
        };
      } catch (fallbackError) {
        return {
          success: false,
          error: fallbackError,
        };
      }
    }
  }

  async _getAkongoStaff() {
    let snap = await db
      .collection("users")
      .where("akongoStaff", "==", true)
      .get();
    let akongoStaff = [];
    snap.forEach((el) => {
      akongoStaff.push(el.data());
    });

    let manager = akongoStaff.filter(
      (el) => el.akongoRank === "manager" || el.akongoRank === "board"
    );

    return {
      manager: manager,
      akongoStaff: akongoStaff,
    };
  }

  // ============================================================
  // Admin User Info Methods (via Cloud Function)
  // ============================================================

  /**
   * Récupère les métadonnées d'authentification Firebase pour une liste d'utilisateurs
   * Utilise une Cloud Function avec Firebase Admin SDK car ces données ne sont pas
   * accessibles côté client.
   *
   * @param {string[]} userIds - Liste des UIDs des utilisateurs
   * @returns {Object} { success: boolean, data?: { [uid]: { lastSignInTime, emailVerified, mfaEnabled, disabled } } }
   */
  async _getUsersAuthInfo(userIds) {
    try {
      if (!userIds || userIds.length === 0) {
        return { success: true, data: {} };
      }

      let cloud = await initFunctions();
      let getUsersAuthInfo = cloud.httpsCallable("getUsersAuthInfo");

      // Parc actif : l'admin y est vérifié, et les invités du parc y sont admis.
      let zooId = null;
      try {
        zooId = await getZooId();
      } catch (_error) {
        zooId = null;
      }
      const result = await getUsersAuthInfo({ userIds, ...(zooId ? { zooId } : {}) });

      if (result.data.success) {
        return {
          success: true,
          data: result.data.data,
        };
      } else {
        console.warn(
          "Erreur getUsersAuthInfo:",
          result.data.message || "Erreur inconnue"
        );
        return {
          success: false,
          message: result.data.message,
          data: {},
        };
      }
    } catch (error) {
      console.error("Erreur lors de l'appel getUsersAuthInfo:", error);
      return {
        success: false,
        error: error.message || error,
        data: {},
      };
    }
  }

  /**
   * Enrichit une liste d'utilisateurs avec leurs métadonnées auth Firebase
   * (lastLogin, emailVerified, mfaEnabled)
   *
   * @param {Array} userList - Liste des utilisateurs avec userId
   * @returns {Array} Liste enrichie avec lastLogin, emailVerified, mfaEnabled
   */
  async _enrichUsersWithAuthInfo(userList) {
    try {
      if (!userList || userList.length === 0) {
        return userList;
      }

      // Extraire les UIDs
      const userIds = userList
        .map((u) => u.userId || u.value)
        .filter((id) => id);

      // Récupérer les infos auth
      const authInfoResult = await this._getUsersAuthInfo(userIds);

      if (!authInfoResult.success || !authInfoResult.data) {
        // En cas d'erreur, retourner la liste originale
        return userList;
      }

      // Enrichir chaque utilisateur
      return userList.map((user) => {
        const uid = user.userId || user.value;
        const authInfo = authInfoResult.data[uid];

        if (authInfo && !authInfo.error) {
          return {
            ...user,
            lastLogin: authInfo.lastSignInTime || 0,
            emailVerified: authInfo.emailVerified || false,
            mfaEnabled: authInfo.mfaEnabled || false,
            authDisabled: authInfo.disabled || false,
          };
        }

        // Si pas d'info auth, garder les valeurs par défaut
        return {
          ...user,
          lastLogin: user.lastLogin || 0,
          emailVerified: user.emailVerified || false,
          mfaEnabled: user.mfaEnabled || false,
        };
      });
    } catch (error) {
      console.error("Erreur enrichissement auth info:", error);
      return userList;
    }
  }
}
